import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import type { TicketDependency, TicketDetail } from '@localagent/sdk';
import { cn } from '@localagent/ui/lib/utils';
import {
  PRIORITY_COLORS,
  PRIORITY_LABELS,
  STATE_COLORS,
  TICKET_STATE_LABELS,
  TICKET_STATUS_LABELS,
} from '../../lib/work-management-helpers';
import { errorWithNothingCachedToShow } from '../../lib/query-error';
import { getSDK } from '../../services/sdk-instance';
import { WorkItemFiles } from './WorkItemFiles';
import { holdsIdentityAtom } from '../../stores/auth/atoms';
import { sdkConfiguredAtom } from '../../stores/sdk/atoms';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

const chipClasses = 'inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium';

function useTicketDetail(ticketId: string | undefined) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<TicketDetail, Error>({
    // Under the ['work','tickets'] prefix so a ticket mutation's invalidation reaches it.
    queryKey: ['work', 'tickets', 'detail', ticketId],
    queryFn: () => getSDK().workManagement.getTicket(ticketId!),
    enabled: Boolean(isAuthenticated && sdkConfigured && ticketId),
  });
}

function DependencyList({
  title,
  dependencies,
  ticketIdOf,
  testId,
}: {
  title: string;
  dependencies: TicketDependency[];
  ticketIdOf: (dependency: TicketDependency) => string;
  testId: string;
}) {
  if (dependencies.length === 0) return null;
  return (
    <section data-testid={testId} data-count={dependencies.length}>
      <h4 className="text-[10px] font-semibold uppercase text-muted-foreground">{title}</h4>
      <ul className="mt-1 space-y-0.5">
        {dependencies.map((dependency) => (
          <li key={dependency.id}>
            <code className="text-[10px] text-muted-foreground">{ticketIdOf(dependency)}</code>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function TicketDetailPanel() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const { data: detail, isLoading, error } = useTicketDetail(ticketId);
  const detailError = errorWithNothingCachedToShow(detail, error);

  if (!ticketId) throw new Error('TicketDetailPanel requires a :ticketId route param');

  const ticket = detail?.ticket;

  return (
    <aside
      className="flex h-full w-96 flex-shrink-0 flex-col overflow-auto border-l bg-background"
      data-testid="ticket-detail-panel"
      data-ticket-id={ticketId}
    >
      <div className="flex items-start justify-between gap-2 border-b px-4 py-3">
        <div className="min-w-0">
          {ticket && (
            <>
              <code className="text-xs text-muted-foreground" data-testid="ticket-detail-identifier">
                {ticket.identifier}
              </code>
              <h3 className="text-sm font-semibold" data-testid="ticket-detail-title">
                {ticket.title}
              </h3>
            </>
          )}
        </div>
        <Link
          to=".."
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="ticket-detail-close"
        >
          Close
        </Link>
      </div>

      <div className="flex-1 space-y-4 p-4">
        {isLoading && <LoadingState label="Loading ticket..." />}
        {detailError && (
          <p className="text-xs text-destructive" data-testid="ticket-detail-error">
            Failed to load ticket: {detailError.message}
          </p>
        )}

        {ticket && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={cn(chipClasses, STATE_COLORS[ticket.state])}>
              {TICKET_STATE_LABELS[ticket.state]}
            </span>
            <span className={cn(chipClasses, PRIORITY_COLORS[ticket.priority])}>
              {PRIORITY_LABELS[ticket.priority]}
            </span>
            {ticket.status && (
              <span className={cn(chipClasses, 'bg-muted text-muted-foreground')}>
                {TICKET_STATUS_LABELS[ticket.status]}
              </span>
            )}
            {!ticket.project_id && (
              <span
                className={cn(chipClasses, 'bg-amber-100 text-amber-800')}
                data-testid="ticket-detail-no-project"
              >
                No project
              </span>
            )}
          </div>
        )}

        {ticket?.description && (
          <p className="whitespace-pre-wrap text-xs" data-testid="ticket-detail-description">
            {ticket.description}
          </p>
        )}

        {detail && (
          <section data-testid="ticket-detail-sessions" data-count={detail.sessions.length}>
            <h4 className="text-[10px] font-semibold uppercase text-muted-foreground">Sessions</h4>
            {detail.sessions.length === 0 ? (
              <p className="mt-1 text-[10px] text-muted-foreground">No sessions yet.</p>
            ) : (
              <ul className="mt-1 space-y-1">
                {detail.sessions.map((session) => (
                  <li key={session.session_id}>
                    <Link
                      to={`/chat/${session.session_id}`}
                      title="Open the session working on this ticket"
                      data-testid={`ticket-detail-session-${session.session_id}`}
                      data-session-id={session.session_id}
                      className={cn(
                        chipClasses,
                        'bg-primary/10 text-primary transition-colors hover:bg-primary/20'
                      )}
                    >
                      Open session
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {detail && <WorkItemFiles workItemKind="ticket" workItemId={ticketId} />}

        {detail && (
          <DependencyList
            title="Blocked by"
            dependencies={detail.blocked_by}
            ticketIdOf={(dependency) => dependency.blocking_ticket_id}
            testId="ticket-detail-blocked-by"
          />
        )}
        {detail && (
          <DependencyList
            title="Blocks"
            dependencies={detail.blocks}
            ticketIdOf={(dependency) => dependency.blocked_ticket_id}
            testId="ticket-detail-blocks"
          />
        )}

        {detail && detail.comments.length > 0 && (
          <section data-testid="ticket-detail-comments" data-count={detail.comments.length}>
            <h4 className="text-[10px] font-semibold uppercase text-muted-foreground">Comments</h4>
            <ul className="mt-1 space-y-2">
              {detail.comments.map((comment) => (
                <li key={comment.id} className="whitespace-pre-wrap text-xs">
                  {comment.body}
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </aside>
  );
}
